import { Injectable } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { ApiService } from "@core/services/api.service";
import { environment } from "environments/environment";
import moment from "moment";
import { BehaviorSubject, Observable, forkJoin, of } from "rxjs";
import {
  catchError,
  map,
  skip,
  subscribeOn,
  switchMap,
  take,
  takeUntil,
} from "rxjs/operators";
import {
  BizonylatKereses,
  Cikktorzs,
  PenzugyiBizonylatTetel,
} from "../../models/torzsek";
import * as _ from "lodash";
import { BizonylatKeresesTipus } from "../../components/torzsek-basic-table/toolbars/bizonylatok-toolbar/bizonylat-torzsek";
import { ArkategoriakService } from "../../services/arkategoriak.service";
import { calcNetto, findByProp, parseJSON, roundToMaxTwoDecimals } from "../../utils";
import { AjanlatService } from "../../services/ajanlat.service";
import { SzallitolevelService } from "../../services/szallitolevel.service";
import { KulsoRaktariEladasService } from "../../services/kulso-raktari-eladas.service";
import { ElolegszamlaService } from "../../services/elolegszamla.service";
import { FoglalasService } from "../../services/foglalas.service";
import { DijbekeroService } from "../../services/dijbekero.service";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { CikktorzsService } from "../../services/cikktorzs.service";
import { newArray } from "@angular/compiler/src/util";

interface SelectedBizonylat {
  readonly [bizonylatId: string]: number;
}

@Injectable()
export class DijbekeroElolegKimenoHelperService {
  public formGroup: FormGroup;
  private readonly BIZONYLAT_ID_ENDPOINT =
    "service/rendeles/bizonylatkeresoidra";

  // Kiválasztott bizonylat id és tétel mennyiség típus szerint
  selectedBizonylatTetelek: {
    a: SelectedBizonylat;
    d: SelectedBizonylat;
    e: SelectedBizonylat;
    f: SelectedBizonylat;
    sz: SelectedBizonylat;
    kr: SelectedBizonylat;
  } = { a: {}, d: {}, e: {}, f: {}, sz: {}, kr: {} };

  // Kiválasztott bizonylatok száma típus szerint
  selectedBizonylatok: {
    a: number;
    d: number;
    e: number;
    sz: number;
    kr: number;
  } = { a: 0, d: 0, e: 0, sz: 0, kr: 0 };

  private arkategoriak: any[];

  private readonly _selectedBizonylatTipus =
    new BehaviorSubject<BizonylatKeresesTipus>(null);
  private readonly _bizonylatok = new BehaviorSubject<any[]>([]);

  private readonly = new BehaviorSubject<any[]>([]);

  get bizonylatok$(): Observable<any[]> {
    return this._bizonylatok.asObservable();
  }

  set selectedBizonylatTipus(tipus: BizonylatKeresesTipus) {
    this._selectedBizonylatTipus.next(tipus);
  }

  constructor(
    private api: ApiService,
    private cikkekService: CikktorzsService,
    private arkategoriakService: ArkategoriakService,
    private elolegszamlaService: ElolegszamlaService,
    private dijbekeroService: DijbekeroService
  ) {}

  fetchData(data: BizonylatKereses): void {
    this.getBizonylatok(data)
      .pipe(
        switchMap((bizonylatok: any[]) => {
          if (data.tipus === 'e') {
            // Filter only the records where statusz == 3
            const itemsToFetch = bizonylatok.filter((b) => b.statusz === 3);
  
            if (itemsToFetch.length === 0) {
              return of(bizonylatok); // No need to fetch additional data
            }
  
            // Create an array of observables for fetching details
            const detailedRequests = itemsToFetch.map((bizonylat) =>
              this.elolegszamlaService.fetchAndMapDataById(bizonylat.id).pipe(
                map((detailedData) => ({ ...bizonylat, ...detailedData })) // Merge details
              )
            );
  
            return forkJoin(detailedRequests).pipe(
              map((detailedResults) => {
                // Merge detailed results back into the original list
                return bizonylatok.map((b) => 
                  b.statusz === 3 ? detailedResults.find((d) => d.id === b.id) || b : b
                );
              })
            );
          }

          if (data.tipus === 'd') {
            // Filter only the records where statusz == 3
            const itemsToFetch = bizonylatok.filter((b) => b.statusz === 3);
  
            if (itemsToFetch.length === 0) {
              return of(bizonylatok); // No need to fetch additional data
            }
  
            // Create an array of observables for fetching details
            const detailedRequests = itemsToFetch.map((bizonylat) =>
              this.dijbekeroService.fetchAndMapDataById(bizonylat.id).pipe(
                map((detailedData) => ({ ...bizonylat, ...detailedData })) // Merge details
              )
            );
  
            return forkJoin(detailedRequests).pipe(
              map((detailedResults) => {
                // Merge detailed results back into the original list
                return bizonylatok.map((b) => 
                  b.statusz === 3 ? detailedResults.find((d) => d.id === b.id) || b : b
                );
              })
            );
          }
  
          return of(bizonylatok); // If tipus is not 'e', return original data
        })
      )
      .subscribe((bizonylatok: any[]) => {
        let mapped = bizonylatok.map((bizonylat: any) => {
          const kiszamlazva_osszeg = data.tipus == 'e' || data.tipus == 'd' ? this.calculateTotalKiszamlazvaOsszeg(bizonylat.tetelek) : 0;
          const afakulcs = bizonylat.tetelek ? bizonylat.tetelek[0]?.afakulcs : null

          const penznem = bizonylat.penznem_id == 1 ? ' Ft' : ' €';
          return ({
            ...bizonylat,
            netto_ar: (bizonylat.netto_ar - kiszamlazva_osszeg)?.toLocaleString('hu-HU', { minimumFractionDigits: 0 }) + penznem,
            brutto_ar: afakulcs == null ? bizonylat.brutto_ar?.toLocaleString('hu-HU', { minimumFractionDigits: 0 }) + penznem : Math.round((bizonylat.netto_ar - kiszamlazva_osszeg) * (1 + (afakulcs / 100)))?.toLocaleString('hu-HU', { minimumFractionDigits: 0 }) + penznem
          })
        });
  
        const user_oe = parseJSON(localStorage.getItem('currentUser'))?.oe;
        mapped = mapped.filter((biz) => biz.oe_id == user_oe);
  
        this._bizonylatok.next(mapped);
        this._selectedBizonylatTipus.next(data.tipus);
  
        this.updateSelectedState();
      });
  }

  // fetchData(data: BizonylatKereses): void {
  //   this.getBizonylatok(data)
  //     .pipe(
  //       // FIXME: Backend FIX kell
  //       map((bizonylatok: any[]) => bizonylatok) //bizonylatok.filter((b: any) => b?.statusz))
  //     )
  //     .subscribe((bizonylatok: any[]) => {
  //       let mapped = data.tipus == 'e' ? bizonylatok.map((bizonylat: any) => {
  //         return {
  //           ...bizonylat,
  //           brutto_ar: bizonylat.brutto_ar.toLocaleString('hu-HU', {minimumFractionDigits: 0}) + ' Ft'
  //         }
  //       }) : bizonylatok;

  //       const user_oe = parseJSON(localStorage.getItem('currentUser'))?.oe;
  //       mapped = mapped.filter((biz) => biz.oe_id == user_oe);

  //       this._bizonylatok.next(mapped);
  //       this._selectedBizonylatTipus.next(data.tipus);

  //       this.updateSelectedState();
  //     });
  // }

  updateSelectedState() {
    const tipus = this._selectedBizonylatTipus.value;

    if (tipus) {
      this.selectedBizonylatTetelek[tipus] = this.groupTetelek(
        this.formGroup?.get("tetelek")?.value,
        tipus
      );
    }
  }

  vanKivalasztottBizonylat(): boolean {
    let sum = 0;

    for (let prop in this.selectedBizonylatok) {
      if (prop !== "null") {
        sum += this.selectedBizonylatok[prop];
      }
    }

    return sum > 0;
  }

  calcSum(
    tetelek: any[],
    penznem: any,
    fizetesi_mod: any,
    kimenoSzamla?: boolean // Kimenő számla esetén az előlegszámlás tételeket nem számoljuk bele a SUM-ba
  ): {
    netto_ar: number;
    brutto_ar: number;
    afa_ar: number;
  } {
    let netto_ar = 0;
    let brutto_ar = 0;
    let afa_ar = 0;

    if (Array.isArray(tetelek)) {
      tetelek.forEach((t: any) => {
        // Hozzáadás
        if (!kimenoSzamla || (kimenoSzamla && !t.elolegszamla_id)) {
          netto_ar += t.netto_ar;
          brutto_ar += t.brutto_ar;
          afa_ar += t.afa_ar;
        }
      });
    }

    const netto = Math.round(netto_ar * 100) / 100;
    const afa = Math.round(afa_ar * 100) / 100;
    const brutto = Math.round(brutto_ar * 100) / 100;

    if (penznem == 1 && fizetesi_mod == 8) {
      return {
        netto_ar: this.checkIfZero(netto, brutto) ? 0 : netto,
        brutto_ar: this.checkIfZero(netto, brutto)
          ? 0
          : this.roundNearest5(brutto),
        afa_ar: this.checkIfZero(netto, brutto) ? 0 : afa,
      };
    } else {
      return {
        netto_ar: this.checkIfZero(netto, brutto) ? 0 : netto,
        brutto_ar: this.checkIfZero(netto, brutto) ? 0 : brutto,
        afa_ar: this.checkIfZero(netto, brutto) ? 0 : afa,
      };
    }
  }

  calcSumHelyesbites(
    tetelek: any[],
    penznem: any,
    fizetesi_mod: any,
    kimenoSzamla?: boolean // Kimenő számla esetén az előlegszámlás tételeket nem számoljuk bele a SUM-ba
  ): {
    netto_ar_hely: number;
    brutto_ar_hely: number;
    afa_ar_hely: number;
  } {
    let netto_ar = 0;
    let brutto_ar = 0;
    let afa_ar = 0;

    if (Array.isArray(tetelek)) {
      tetelek.forEach((t: any) => {
        // Hozzáadás
        if (t.cikkszam != 'vev001') {
          netto_ar += t.netto_ar;
          brutto_ar += t.brutto_ar;
          afa_ar += t.afa_ar;
        }
      });
    }

    const netto = Math.round(netto_ar * 100) / 100;
    const afa = Math.round(afa_ar * 100) / 100;
    const brutto = Math.round(brutto_ar * 100) / 100;

    if (penznem == 1 && fizetesi_mod == 8) {
      return {
        netto_ar_hely: netto,
        brutto_ar_hely: this.roundNearest5(brutto),
        afa_ar_hely: afa,
      };
    } else {
      return {
        netto_ar_hely: netto,
        brutto_ar_hely: brutto,
        afa_ar_hely: afa,
      };
    }
  }

      calcSumHelyesbites2(
    tetelek: any[],
    penznem: any,
    fizetesi_mod: any,
    kimenoSzamla?: boolean // Kimenő számla esetén az előlegszámlás tételeket nem számoljuk bele a SUM-ba
  ): {
    netto_ar: number;
    brutto_ar: number;
    afa_ar: number;
  } {
    let netto_ar = 0;
    let brutto_ar = 0;
    let afa_ar = 0;

    if (Array.isArray(tetelek)) {
      tetelek.forEach((t: any) => {
        // Hozzáadás
        if (t.cikkszam != 'vev001') {
          netto_ar += t.netto_ar;
          brutto_ar += t.brutto_ar;
          afa_ar += t.afa_ar;
        }
      });
    }

    const netto = Math.round(netto_ar * 100) / 100;
    const afa = Math.round(afa_ar * 100) / 100;
    const brutto = Math.round(brutto_ar * 100) / 100;

    if (penznem == 1 && fizetesi_mod == 8) {
      return {
        netto_ar: netto,
        brutto_ar: this.roundNearest5(brutto),
        afa_ar: afa,
      };
    } else {
      return {
        netto_ar: netto,
        brutto_ar: brutto,
        afa_ar: afa,
      };
    }
  }

  checkIfZero(netto: number, brutto: number): boolean {
    return netto == 0 || brutto == 0;
  }

  roundNearest5(num) {
    return Math.round(num / 5) * 5;
  }

  mapTetelekWithReferenceId(
    tetelek: PenzugyiBizonylatTetel[],
    id: number,
    propName: BizonylatKeresesTipus,
    penznem_id?: any,
    bizonylat_szam?: any
  ): PenzugyiBizonylatTetel[] {
    // Return list compatible tételek
    return tetelek.map((t: any) => {
      console.log('tetel', t)
      let netto_eloleg, afa_eloleg, brutto_eloleg, netto_eloleg_egysegar, afa_eloleg_egysegar, brutto_eloleg_egysegar = 0;
      let netto_dijbekero, afa_dijbekero, brutto_dijbekero, netto_dijbekero_egysegar, afa_dijbekero_egysegar, brutto_dijbekero_egysegar = 0;
      const maradek = propName == 'd' ? t?.mennyiseg : t?.mennyiseg - (t?.kiszamlazva || 0);
      const kedvezmeny = t.kedvezmeny ? 1 - t.kedvezmeny / 100 : 1;

      const netto =
        penznem_id == 1
          ? Math.round(t.netto_egysegar * maradek * kedvezmeny)
          : t.netto_egysegar * maradek * kedvezmeny;

      const afa =
        penznem_id == 1
          ? Math.round(netto * (t.afakulcs / 100))
          : netto * (t.afakulcs / 100);

      if(propName == 'd') {
        netto_dijbekero_egysegar = (t.mennyiseg - t.kiszamlazva) == 0 ? roundToMaxTwoDecimals((t.netto_ar - t.kiszamlazva_osszeg) / t.mennyiseg) : roundToMaxTwoDecimals((t.netto_ar - t.kiszamlazva_osszeg) / (t.mennyiseg - t.kiszamlazva));
        afa_dijbekero_egysegar = roundToMaxTwoDecimals(netto_dijbekero_egysegar * (t.afakulcs / 100));
        brutto_dijbekero_egysegar = roundToMaxTwoDecimals(netto_dijbekero_egysegar + afa_dijbekero_egysegar);

        netto_dijbekero = Math.round(t.netto_ar - t.kiszamlazva_osszeg);
        afa_dijbekero = Math.round(netto_dijbekero * (t.afakulcs / 100));
        brutto_dijbekero = Math.round(netto_dijbekero + afa_dijbekero);

      } else if((t.cikk_id >= 500 && t.cikk_id <= 512)) {
        netto_eloleg_egysegar = roundToMaxTwoDecimals(t.netto_ar - t.kiszamlazva_osszeg);
        afa_eloleg_egysegar = roundToMaxTwoDecimals(netto_eloleg_egysegar * (t.afakulcs / 100));
        brutto_eloleg_egysegar = roundToMaxTwoDecimals(netto_eloleg_egysegar + afa_eloleg_egysegar);

        netto_eloleg = Math.round(t.netto_ar - t.kiszamlazva_osszeg);
        afa_eloleg = Math.round(netto_eloleg * (t.afakulcs / 100));
        brutto_eloleg = Math.round(netto_eloleg + afa_eloleg);
      }

      if(propName == 'd') {
        return{
          ...t,
          mennyiseg: (t?.mennyiseg - t?.kiszamlazva) == 0 ? 1 : t?.mennyiseg - t?.kiszamlazva,
          netto_egysegar: netto_dijbekero_egysegar,
          brutto_egysegar: brutto_dijbekero_egysegar,
          netto_ar: netto_dijbekero,
          afa_ar: afa_dijbekero,
          brutto_ar: (brutto_dijbekero),
          netto_dijbekero: netto_dijbekero, 
          [this.getPropNameByTipus(propName)]: id,
        }
      }

      //Ha a tétel a Vevőtől kapott előleg akkor más a visszatérés
      return t.cikk_id == 1 || (t.cikk_id >= 500 && t.cikk_id <= 512) ? 
      {
        ...t,
        cikknev: t.cikknev + ' ' + bizonylat_szam,
        mennyiseg: 1,
        netto_egysegar: -netto_eloleg_egysegar,
        brutto_egysegar: -brutto_eloleg_egysegar,
        netto_ar: -netto_eloleg,
        afa_ar: -afa_eloleg,
        brutto_ar: -(brutto_eloleg),
        elolegszamla_brutto: brutto_eloleg,
        elolegszamla_netto: netto_eloleg,
        [this.getPropNameByTipus(propName)]: id,
      } : 
      {
        ...t,
        mennyiseg: t?.mennyiseg - (t?.kiszamlazva || 0),
        netto_ar: netto,
        afa_ar: afa,
        brutto_ar: netto + afa,
        [this.getPropNameByTipus(propName)]: id,
      };
    });
  }

  getPropNameByTipus(
    tipus: BizonylatKeresesTipus
  ):
    | "ajanlat_id"
    | "dijbekero_id"
    | "elolegszamla_id"
    | "foglalas_id"
    | "szallitolevel_id"
    | "kulsoraktarieladas_id" {
    switch (tipus) {
      case "a": {
        return "ajanlat_id";
      }

      case "d": {
        return "dijbekero_id";
      }

      case "e": {
        return "elolegszamla_id";
      }

      case "f": {
        return "foglalas_id";
      }

      case "sz": {
        return "szallitolevel_id";
      }

      case "kr": {
        return "kulsoraktarieladas_id";
      }

      default: {
        return null;
      }
    }
  }

  groupTetelek(
    tetelek: PenzugyiBizonylatTetel[],
    tipus: BizonylatKeresesTipus
  ): any {
    const propName = this.getPropNameByTipus(tipus);

    let grouped = _.groupBy(tetelek, propName);
    let result: any = {};

    // Map object's array value to array.length value
    Object.keys(grouped)
      .filter((key: string) => key !== "undefined" && key !== "null")
      .forEach((key: string) => {
        result[key] = (grouped[key] as PenzugyiBizonylatTetel[]).reduce(
          (sum, t) => sum + t.mennyiseg,
          0
        );
      });

    // Update Bizonylat selection
    this.selectedBizonylatok[tipus] = Object.keys(result)?.length;

    return result;
  }

  filterCikkekByMennyiseg(cikkek: Cikktorzs[]): Cikktorzs[] {
    return cikkek.filter((c: Cikktorzs) => c.aktualis_mennyiseg > 0);
  }

  private getBizonylatok(data: any): Observable<any[]> {
    return data.partner_id ? this.api
      .get(
        `${environment.bizonylatkereso}/${data.tipus}/${
          data.partner_id
        }/${moment(data.kezdete).format("YYYY-MM-DD")}/${moment(
          data.vege
        ).format("YYYY-MM-DD")}`
      )
      .pipe(
        catchError((err) => {
          console.log("Bizonylatkereső error", err);

          return of([]);
        })
      ) : of([]);
  }

  calcMennyisegByCikk(tetelek: any[]): any {
    let maradek = [];

    tetelek.forEach((tetel) => {
      const osszesen = tetelek
        .filter(
          (item) =>
            item.cikk_id === tetel.cikk_id &&
            !item.szallitolevel_id &&
            !item.foglalas_id
        )
        .reduce((sum, current) => sum + current.mennyiseg, 0);
      const eredetiOsszesen = tetelek
        .filter(
          (item) => item.cikk_id === tetel.cikk_id && item.szallitolevel_id
        )
        .reduce((sum, current) => sum + current.mennyiseg, 0);

      if (maradek.findIndex((item) => item.cikk_id == tetel.cikk_id) == -1) {
        maradek.push({
          cikk_id: tetel.cikk_id,
          osszesen: osszesen,
          eredetiOsszesen: eredetiOsszesen,
        });
      }
    });

    return maradek;
  }

  betweenDates(startDate: Date, endDate: Date, bizonylat: any) {
    const bizDate = new Date(bizonylat.letrehozva);
    const startDateConst = new Date(startDate);
    const endDateConst = new Date(endDate);

    if (bizDate && startDateConst && endDateConst) {
      if (bizDate >= startDateConst && bizDate <= endDateConst) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }

  fetchBizonylatById(tipus: string, id: number): Observable<any> {
    return forkJoin([
      this.api.get(`${this.BIZONYLAT_ID_ENDPOINT}/${tipus}/${id}`).pipe(
        take(1),
        catchError((err) => {
          console.log("Bizonylatkereső error", err);

          return of([]);
        })
      ),
      this.cikkekService.initData$,
      this.arkategoriakService.initData$,
    ]).pipe(
      map(([bizonylat, cikkek, arkategoriak]) => {
        return {
          ...bizonylat,
          tetelek: bizonylat.tetelek.map((tetel) => {
            return {
              ...tetel,
              cikknev: findByProp(cikkek, "id", tetel.cikk_id)?.cikknev,
              arkategoria: findByProp(arkategoriak, "id", tetel.arkategoria_id)
                ?.nev,
            };
          }),
        };
      })
    );
  }

  createElolegSzamlaSor(row: any, netto_ar: number, afa_ar: number, brutto_ar: number): any {
    return {
      ...row,
      id: null,
      //elolegszamla_id: null,
      cikk_id: 1,
      cikkszam: "vev001",
      cikknev: "Vevőtől kapott előleg ",
      mennyiseg: 1,
      mennyisegi_egyseg: 2,
      mennyisegi_egyseg_text: "db",
      me_text: "db / dob / db",
      me_id: 2,
      me_id_2: 36,
      me_id_3: 2,
      brutto_egysegar: brutto_ar * (-1),
      netto_egysegar: netto_ar * (-1),
      netto_ar: netto_ar * (-1),
      afa_ar: afa_ar * (-1),
      afa_egysegar: afa_ar * (-1),
      brutto_ar: brutto_ar * (-1),
    };
  }

  private calculateTotalKiszamlazvaOsszeg(bizonylatok: any[]): number {
    if(bizonylatok) {
      return bizonylatok.reduce((total, bizonylat) => total + bizonylat.kiszamlazva_osszeg, 0);
    } else {
      return 0
    }
  }

}
